import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Button, Input, Popover, Drawer, message } from 'antd';
import { Solar, Lunar, LunarMonth, LunarYear } from 'lunar-typescript';
import { CalendarOutlined, ClockCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import './BirthDateTimePicker.css';

interface BirthDateTimePickerProps {
  locale?: 'zh-CN' | 'en-US';
  value?: {
    date: dayjs.Dayjs;
    isLunar: boolean;
    hourUnknown?: boolean;
    minuteUnknown?: boolean;
  };
  onChange?: (value: { date: dayjs.Dayjs; isLunar: boolean; hourUnknown?: boolean; minuteUnknown?: boolean }) => void;
  allowLunar?: boolean;
}

interface WheelOption {
  label: string;
  value: number | string;
}

const WheelColumn: React.FC<{
  options: WheelOption[];
  value: number | string;
  onChange: (value: any) => void;
}> = ({ options, value, onChange }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef.current) {
      const index = options.findIndex((opt) => opt.value === value);
      if (index !== -1) {
        containerRef.current.scrollTop = index * 40;
      }
    }
  }, [value, options]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const scrollTop = e.currentTarget.scrollTop;
    const index = Math.round(scrollTop / 40);
    if (index >= 0 && index < options.length) {
      const newValue = options[index].value;
      if (newValue !== value) {
        onChange(newValue);
      }
    }
  };

  return (
    <div 
      className="wheel-column" 
      ref={containerRef}
      onScroll={handleScroll}
    >
      {options.map((opt) => (
        <div 
          key={`${opt.value}-${opt.label}`} 
          className={`wheel-item ${opt.value === value ? 'selected' : ''}`}
          onClick={() => onChange(opt.value)}
        >
          {opt.label}
        </div>
      ))}
    </div>
  );
};

const LUNAR_MONTH_LABELS = ['正月', '二月', '三月', '四月', '五月', '六月', '七月', '八月', '九月', '十月', '冬月', '腊月'];
const LUNAR_DAY_LABELS = ['初一', '初二', '初三', '初四', '初五', '初六', '初七', '初八', '初九', '初十', '十一', '十二', '十三', '十四', '十五', '十六', '十七', '十八', '十九', '二十', '廿一', '廿二', '廿三', '廿四', '廿五', '廿六', '廿七', '廿八', '廿九', '三十'];

function formatWheelNumber(value: number) {
  return value < 10 ? `0${value}` : `${value}`;
}

function getLunarMonthLabel(month: number) {
  const index = Math.abs(month) - 1;
  const base = LUNAR_MONTH_LABELS[index] || `${Math.abs(month)}月`;
  return month < 0 ? `闰${base}` : base;
}

function getLunarDayLabel(day: number) {
  return LUNAR_DAY_LABELS[day - 1] || `${day}`;
}

function getDisplayText(date: dayjs.Dayjs, lunarMode: boolean) {
  const hourText = formatWheelNumber(date.hour());
  const minuteText = formatWheelNumber(date.minute());
  const timeText = `${hourText}:${minuteText}`;

  if (!lunarMode) {
    return `${date.format('YYYY-MM-DD')} ${timeText}`;
  }

  const solar = Solar.fromYmd(date.year(), date.month() + 1, date.date());
  const lunar = solar.getLunar();
  return `${lunar.getYear()}年${getLunarMonthLabel(lunar.getMonth())}${lunar.getDayInChinese()} ${timeText}`;
}

const BirthDateTimePicker: React.FC<BirthDateTimePickerProps> = ({ value, onChange, locale = 'zh-CN', allowLunar = true }) => {
  const [open, setOpen] = useState(false);
  const [isLunar, setIsLunar] = useState(allowLunar && (value?.isLunar || false));
  const [tempDate, setTempDate] = useState(value?.date || dayjs());
  const [quickInput, setQuickInput] = useState('');
  const [isMobile, setIsMobile] = useState(false);

  // 检测是否为移动端
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // 状态：选中的年月日时分
  const [year, setYear] = useState(tempDate.year());
  const [month, setMonth] = useState(tempDate.month() + 1);
  const [day, setDay] = useState(tempDate.date());
  const [hour, setHour] = useState<number | string>(tempDate.hour());
  const [minute, setMinute] = useState<number | string>(tempDate.minute());
  // 农历状态（如果处于农历模式）
  const [lunarYear, setLunarYear] = useState(0);
  const [lunarMonth, setLunarMonth] = useState(0);
  const [lunarDay, setLunarDay] = useState(0);
  const [isLeap, setIsLeap] = useState(false);

  useEffect(() => {
    if (value) {
      const lunarMode = allowLunar && value.isLunar;
      setTempDate(value.date);
      setIsLunar(lunarMode);
      syncToPickers(value.date, lunarMode);
    }
  }, [value, open, allowLunar]);

  useEffect(() => {
    if (!allowLunar && isLunar) setIsLunar(false);
  }, [allowLunar, isLunar]);

  const syncToPickers = (date: dayjs.Dayjs, lunarMode: boolean) => {
    const y = date.year();
    const m = date.month() + 1;
    const d = date.date();
    const h = date.hour();
    const min = date.minute();

    setYear(y);
    setMonth(m);
    setDay(d);
    setHour(h);
    setMinute(min);

    if (lunarMode) {
      const solar = Solar.fromYmd(y, m, d);
      const lunar = solar.getLunar();
      setLunarYear(lunar.getYear());
      setLunarMonth(Math.abs(lunar.getMonth()));
      setLunarDay(lunar.getDay());
      setIsLeap(lunar.getMonth() < 0);
    }
  };

  const handleConfirm = () => {
    let finalDate: dayjs.Dayjs;
    const resolvedHour = Number(hour);
    const resolvedMinute = Number(minute);
    if (isLunar) {
      const lunar = Lunar.fromYmd(lunarYear, isLeap ? -lunarMonth : lunarMonth, lunarDay);
      const solar = lunar.getSolar();
      finalDate = dayjs(`${solar.getYear()}-${solar.getMonth().toString().padStart(2, '0')}-${solar.getDay().toString().padStart(2, '0')} ${resolvedHour.toString().padStart(2, '0')}:${resolvedMinute.toString().padStart(2, '0')}`);
    } else {
      finalDate = dayjs(`${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')} ${resolvedHour.toString().padStart(2, '0')}:${resolvedMinute.toString().padStart(2, '0')}`);
    }
    
    if (onChange) {
      onChange({ date: finalDate, isLunar, hourUnknown: false, minuteUnknown: false });
    }
    setOpen(false);
  };

  const handleCancel = () => {
    if (value) {
      syncToPickers(value.date, value.isLunar);
      setIsLunar(value.isLunar);
    }
    setOpen(false);
  };

  const handleNow = () => {
    const now = dayjs();
    syncToPickers(now, isLunar);
  };

  const handleQuickInput = () => {
    if (quickInput.length >= 8) {
      const y = parseInt(quickInput.substring(0, 4));
      const m = parseInt(quickInput.substring(4, 6));
      const d = parseInt(quickInput.substring(6, 8));
      const h = quickInput.length >= 10 ? parseInt(quickInput.substring(8, 10)) : 0;
      const min = quickInput.length >= 12 ? parseInt(quickInput.substring(10, 12)) : 0;

      if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
        if (isLunar) {
          try {
            const lunar = Lunar.fromYmd(y, isLeap ? -m : m, d);
            const solar = lunar.getSolar();
            const newDate = dayjs(`${solar.getYear()}-${solar.getMonth().toString().padStart(2, '0')}-${solar.getDay().toString().padStart(2, '0')} ${h.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`);
            setLunarYear(y);
            setLunarMonth(m);
            setLunarDay(d);
            syncToPickers(newDate, true);
          } catch {
            message.error(locale === 'zh-CN' ? '农历日期格式错误，闰月请使用滚轮选择' : 'Invalid lunar date. Please use the wheel to select leap months.');
          }
        } else {
          const newDate = dayjs(`${y}-${m.toString().padStart(2, '0')}-${d.toString().padStart(2, '0')} ${h.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`);
          syncToPickers(newDate, false);
        }
      } else {
        message.error(locale === 'zh-CN' ? '日期格式错误' : 'Invalid date format');
      }
    }
  };

  // 生成选项
  const years = useMemo<WheelOption[]>(() => Array.from({ length: 201 }, (_, i) => {
    const optionYear = 1900 + i;
    return { value: optionYear, label: `${optionYear}` };
  }), []);
  const months = useMemo<WheelOption[]>(() => Array.from({ length: 12 }, (_, i) => {
    const optionMonth = i + 1;
    return { value: optionMonth, label: formatWheelNumber(optionMonth) };
  }), []);
  const lunarMonths = useMemo<WheelOption[]>(() => {
    if (!lunarYear) return months;
    return LunarYear.fromYear(lunarYear)
      .getMonthsInYear()
      .map((item) => ({
        value: item.getMonth(),
        label: getLunarMonthLabel(item.getMonth()),
      }));
  }, [lunarYear, months]);
  const days = useMemo(() => {
    let maxDays = 31;
    if (isLunar) {
      const lm = LunarMonth.fromYm(lunarYear, isLeap ? -lunarMonth : lunarMonth);
      maxDays = lm ? lm.getDayCount() : 30;
    } else {
      maxDays = dayjs(`${year}-${month}`).daysInMonth();
    }
    return Array.from({ length: maxDays }, (_, i) => {
      const optionDay = i + 1;
      return { value: optionDay, label: isLunar ? getLunarDayLabel(optionDay) : formatWheelNumber(optionDay) };
    });
  }, [year, month, lunarYear, lunarMonth, isLeap, isLunar]);

  const hours = useMemo<WheelOption[]>(() => Array.from({ length: 24 }, (_, i) => ({
    value: i,
    label: formatWheelNumber(i),
    })), []);
  const minutes = useMemo<WheelOption[]>(() => Array.from({ length: 60 }, (_, i) => ({
    value: i,
    label: formatWheelNumber(i),
    })), []);

  useEffect(() => {
    if (!isLunar || lunarMonths.length === 0) return;

    const currentLunarMonth = isLeap ? -lunarMonth : lunarMonth;
    const hasCurrentMonth = lunarMonths.some((item) => item.value === currentLunarMonth);
    if (!hasCurrentMonth) {
      const fallbackMonth = Number(lunarMonths[0].value);
      setLunarMonth(Math.abs(fallbackMonth));
      setIsLeap(fallbackMonth < 0);
    }
  }, [isLunar, lunarMonths, isLeap, lunarMonth]);

  useEffect(() => {
    const maxDay = days.length;
    if (day > maxDay) {
      setDay(maxDay);
    }
    if (isLunar && lunarDay > maxDay) {
      setLunarDay(maxDay);
    }
  }, [days, day, lunarDay, isLunar]);

  const toggleLunar = (lunar: boolean) => {
    if (!allowLunar || lunar === isLunar) return;
    
    if (lunar) {
      // 公历转农历
      const solar = Solar.fromYmd(year, month, day);
      const lunarObj = solar.getLunar();
      setLunarYear(lunarObj.getYear());
      setLunarMonth(Math.abs(lunarObj.getMonth()));
      setLunarDay(lunarObj.getDay());
      setIsLeap(lunarObj.getMonth() < 0);
    } else {
      // 农历转公历
      const lunarObj = Lunar.fromYmd(lunarYear, isLeap ? -lunarMonth : lunarMonth, lunarDay);
      const solar = lunarObj.getSolar();
      setYear(solar.getYear());
      setMonth(solar.getMonth());
      setDay(solar.getDay());
    }
    setIsLunar(lunar);
  };

  const pickerContent = (
    <div className="birth-picker-container" onClick={e => e.stopPropagation()}>
      <div className="birth-picker-header">
        <Button type="text" onClick={handleCancel} className="cancel-text">{locale === 'zh-CN' ? '取消' : 'Cancel'}</Button>
        <div className="picker-tabs">
          <Button 
            className={`tab-item ${!isLunar ? 'active' : ''}`} 
            onClick={() => toggleLunar(false)}
          >{locale === 'zh-CN' ? '公历' : 'Solar'}</Button>
          {allowLunar && <Button 
            className={`tab-item ${isLunar ? 'active' : ''}`} 
            onClick={() => toggleLunar(true)}
          >{locale === 'zh-CN' ? '农历' : 'Lunar'}</Button>}
        </div>
        <div className="header-actions">
          <Button shape="circle" onClick={handleNow} className="now-btn">{locale === 'zh-CN' ? '今' : 'Now'}</Button>
        </div>
      </div>

      <div className="quick-input-row">
        <Input 
          placeholder={
            locale === 'zh-CN'
              ? (isLunar ? '输入农历年月日时分(格式199303270255，闰月请用滚轮选)' : '输入公历年月日时分(格式199303270255)')
              : (isLunar ? 'Enter lunar date and time (199303270255, use wheel for leap month)' : 'Enter solar date and time (199303270255)')
          } 
          value={quickInput}
          onChange={e => setQuickInput(e.target.value)}
          suffix={<Button type="text" onClick={handleQuickInput} className="query-confirm-btn">{locale === 'zh-CN' ? '确定' : 'Confirm'}</Button>}
        />
      </div>

      <div className="wheel-picker-labels">
        <span>{locale === 'zh-CN' ? '年' : 'Y'}</span>
        <span>{locale === 'zh-CN' ? '月' : 'M'}</span>
        <span>{locale === 'zh-CN' ? '日' : 'D'}</span>
        <span>{locale === 'zh-CN' ? '时' : 'H'}</span>
        <span>{locale === 'zh-CN' ? '分' : 'Min'}</span>
      </div>

      <div className="wheel-picker-container">
        <div className="wheel-overlay-top"></div>
        <div className="wheel-overlay-bottom"></div>
        
        <WheelColumn options={years} value={isLunar ? lunarYear : year} onChange={val => isLunar ? setLunarYear(val) : setYear(val)} />
        <WheelColumn
          options={isLunar ? lunarMonths : months}
          value={isLunar ? (isLeap ? -lunarMonth : lunarMonth) : month}
          onChange={(val) => {
            if (isLunar) {
              const nextMonth = Number(val);
              setLunarMonth(Math.abs(nextMonth));
              setIsLeap(nextMonth < 0);
            } else {
              setMonth(val);
            }
          }}
        />
        <WheelColumn options={days} value={isLunar ? lunarDay : day} onChange={val => isLunar ? setLunarDay(val) : setDay(val)} />
        <WheelColumn
          options={hours}
          value={hour}
          onChange={(val) => {
            setHour(val);
          }}
        />
        <WheelColumn
          options={minutes}
          value={minute}
          onChange={(val) => {
            setMinute(val);
          }}
        />
      </div>

      <div className="picker-bottom-section">
        <Button type="primary" onClick={handleConfirm} className="main-confirm-btn">{locale === 'zh-CN' ? '确定' : 'Confirm'}</Button>
      </div>
    </div>
  );

  const displayDate = value?.date ? (
      <div className="picker-display-value">
      <CalendarOutlined style={{ marginRight: 8, color: '#d4af6a' }} />
      <span>{getDisplayText(value.date, value.isLunar)}</span>
      <span className="lunar-tag">{value.isLunar ? (locale === 'zh-CN' ? ' (农历)' : ' (Lunar)') : (locale === 'zh-CN' ? ' (公历)' : ' (Solar)')}</span>
    </div>
  ) : (
    <span className="picker-placeholder">{locale === 'zh-CN' ? '请选择出生时间' : 'Please select birth time'}</span>
  );

  // 移动端使用 Drawer
  if (isMobile) {
    return (
      <>
        <div className="picker-trigger-box" onClick={() => setOpen(true)}>
          {displayDate}
          <ClockCircleOutlined style={{ color: '#999' }} />
        </div>
        <Drawer
          open={open}
          onClose={handleCancel}
          placement="bottom"
          height="auto"
          closable={false}
          className="birth-picker-drawer"
          styles={{
            body: { padding: 0 }
          }}
        >
          {pickerContent}
        </Drawer>
      </>
    );
  }

  // PC 端使用 Popover
  return (
    <Popover
      content={pickerContent}
      trigger="click"
      open={open}
      onOpenChange={setOpen}
      placement="bottomLeft"
      overlayClassName="birth-picker-popover"
      destroyTooltipOnHide
    >
      <div className="picker-trigger-box">
        {displayDate}
        <ClockCircleOutlined style={{ color: '#999' }} />
      </div>
    </Popover>
  );
};

export default BirthDateTimePicker;
